import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Phone, Mail, MapPin, Globe, Shield, Smartphone, Camera, Users, Award, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const Index = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-sm border-b border-blue-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">Fremont Tech</span>
              <span className="text-sm text-gray-500">프리몬테크</span>
            </div>
            <div className="hidden md:flex space-x-8">
              <Link to="/about" className="text-gray-700 hover:text-blue-600 transition-colors">ABOUT US</Link>
              <Link to="/services" className="text-gray-700 hover:text-blue-600 transition-colors">WHAT WE DO</Link>
              <Link to="/contact" className="text-gray-700 hover:text-blue-600 transition-colors">CONTACT US</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-800 hover:bg-blue-200">
              미국 실리콘밸리 10년 경험
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              신뢰할 수 있는 <span className="text-blue-600">기술 파트너</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              웹사이트 제작, 앱 개발, CCTV 상담 및 설치까지<br />
              사용자 중심의 전문적인 서비스를 제공합니다
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3">
                <Phone className="mr-2 h-5 w-5" />
                무료 상담 받기
              </Button>
              <Button variant="outline" size="lg" className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3">
                서비스 보기
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">전문 서비스</h2>
            <p className="text-xl text-gray-600">고객의 성공을 위한 맞춤형 솔루션</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover:shadow-lg transition-shadow border-blue-100">
              <CardHeader>
                <Globe className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle className="text-xl">웹사이트 제작</CardTitle>
                <CardDescription>
                  반응형 웹사이트부터 복잡한 웹 애플리케이션까지
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• 반응형 디자인</li>
                  <li>• SEO 최적화</li>
                  <li>• 사용자 경험 중심</li>
                  <li>• 유지보수 지원</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow border-blue-100">
              <CardHeader>
                <Smartphone className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle className="text-xl">앱 개발</CardTitle>
                <CardDescription>
                  iOS, Android 네이티브 앱 및 크로스 플랫폼 앱
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• 네이티브 앱 개발</li>
                  <li>• 크로스 플랫폼</li>
                  <li>• UI/UX 디자인</li>
                  <li>• 앱스토어 배포</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow border-blue-100">
              <CardHeader>
                <Camera className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle className="text-xl">CCTV 상담 및 설치</CardTitle>
                <CardDescription>
                  보안 카메라 시스템 전문 컨설팅 및 설치
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• 네트워크 카메라 시스템</li>
                  <li>• 무료 컨설팅</li>
                  <li>• 전지역 방문 설치</li>
                  <li>• 사후 관리 서비스</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">풍부한 경험</h2>
            <p className="text-xl text-gray-600">미국 실리콘밸리에서 쌓은 10년의 노하우</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-md">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">대기업 프로젝트</h3>
              <p className="text-gray-600 text-sm">Walmart, FedEx, DHL, Nike 등<br />대기업 CCTV 설치 관리</p>
            </div>
            <div className="text-center">
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-md">
                <Award className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">공공기관 경험</h3>
              <p className="text-gray-600 text-sm">지역 학교, 도서관,<br />커뮤니티 센터 공사</p>
            </div>
            <div className="text-center">
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-md">
                <Clock className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">10년 경험</h3>
              <p className="text-gray-600 text-sm">미국 실리콘밸리에서<br />10여년간 운영</p>
            </div>
            <div className="text-center">
              <div className="bg-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-md">
                <Shield className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">사용자 중심</h3>
              <p className="text-gray-600 text-sm">사용자의 입장을<br />잘 고려한 서비스</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">지금 바로 상담받으세요</h2>
          <p className="text-xl text-gray-600 mb-12">
            "한 번 고객은 영원한 친구입니다"<br />
            무료 상담을 통해 최적의 솔루션을 제안해드립니다
          </p>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="flex flex-col items-center">
              <Phone className="h-8 w-8 text-blue-600 mb-3" />
              <h3 className="font-semibold mb-2">전화 상담</h3>
              <p className="text-gray-600">010-2864-1942</p>
            </div>
            <div className="flex flex-col items-center">
              <Mail className="h-8 w-8 text-blue-600 mb-3" />
              <h3 className="font-semibold mb-2">이메일</h3>
              <p className="text-gray-600">hyungwoo@naver.com</p>
            </div>
            <div className="flex flex-col items-center">
              <MapPin className="h-8 w-8 text-blue-600 mb-3" />
              <h3 className="font-semibold mb-2">오피스</h3>
              <p className="text-gray-600 text-sm">대전 중구 대종로 550번길 5<br />유원오피스텔 1008호</p>
            </div>
          </div>
          <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-4 text-lg">
            <Phone className="mr-2 h-5 w-5" />
            무료 상담 신청하기
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Shield className="h-6 w-6 text-blue-400" />
                <span className="text-xl font-bold">Fremont Tech</span>
                <span className="text-sm text-gray-400">프리몬테크</span>
              </div>
              <p className="text-gray-400 mb-4">
                신뢰할 수 있는 기술 파트너로서 고객의 성공을 위한<br />
                최고의 솔루션을 제공합니다.
              </p>
              <p className="text-sm text-gray-500">
                © 2024 Fremont Tech. All rights reserved.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">연락처</h3>
              <div className="space-y-2 text-gray-400">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>010-2864-1942</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>hyungwoo@naver.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4" />
                  <span>대전 중구 대종로 550번길 5 유원오피스텔 1008호</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Globe className="h-4 w-4" />
                  <span>newvision.co.kr</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
